import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faProjectDiagram,
  faFolder,
  faFileAlt,
  faCheck,
  faBolt,
  faSearch,
  faTimes,
  faExclamationTriangle,
  faClock,
  faSatellite,
  faGlobe,
  faCloud,
  faMountain,
  faRuler,
  faRobot,
  faFile,
  faRefresh,
  faStream,
  faPlay,
  faPause,
  faFilter,
  faChartLine,
  faCodeBranch,
  faCogs,
  faChevronDown,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import apiService from '../services/api';
import LoadingScreen from './LoadingScreen';

const Pipelines = () => {
  const [expandedPipelines, setExpandedPipelines] = useState(new Set(['pipeline-ecmwf-cdo']));
  const [pipelineTabs, setPipelineTabs] = useState({});
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [pipelineData, setPipelineData] = useState([]);
  const [validationRules, setValidationRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFailedModal, setShowFailedModal] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState(null);

  // Set active tab for a specific pipeline
  const setPipelineTab = (pipelineId, tab) => {
    setPipelineTabs(prev => ({
      ...prev,
      [pipelineId]: tab
    }));
  };

  // Get active tab for a pipeline (default: 'overview')
  const getPipelineTab = (pipelineId) => {
    return pipelineTabs[pipelineId] || 'overview';
  };

  // Mock failed items data
  const failedItems = [
    {
      id: 1,
      filename: 'S2A_MSIL2A_20240315T092031_T34SGD_corrupt.SAFE',
      mission: 'Sentinel-2',
      error: 'CRS metadata missing',
      details: 'Expected EPSG:32634 (WGS84 / UTM Zone 34N) in metadata, but CRS information not found in MTD_MSIL2A.xml',
      timestamp: '2025-10-02 14:23:15',
      affectedBands: ['B04', 'B08'],
      fileSize: '1.2 GB'
    },
    {
      id: 2,
      filename: 'LC08_L2SP_175032_20240315_incomplete.tif',
      mission: 'Landsat-8',
      error: 'Cloud coverage exceeds threshold',
      details: 'Cloud coverage: 78% exceeds maximum allowed threshold of 30% for agricultural monitoring pipeline',
      timestamp: '2025-10-02 13:45:22',
      cloudCover: '78%',
      fileSize: '850 MB'
    },
    {
      id: 3,
      filename: 'MOD13Q1_A2024270_invalid.hdf',
      mission: 'MODIS',
      error: 'NoData values exceed 50% of pixels',
      details: 'Quality assessment shows 67% of pixels flagged as NoData or fill values. Likely incomplete acquisition or processing failure.',
      timestamp: '2025-10-02 12:18:47',
      noDataPercentage: '67%',
      fileSize: '456 MB'
    }
  ];

  // Load pipeline data and validation rules from API when component mounts
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [pipelinesData, rulesData] = await Promise.all([
          apiService.getPipelines(),
          apiService.getValidationRules()
        ]);
        setPipelineData(pipelinesData);
        setValidationRules(rulesData);

        // Random loading time between 1-2.5 seconds
        const loadingTime = 1000 + Math.random() * 1500;
        setTimeout(() => {
          setLoading(false);
        }, loadingTime);
      } catch (err) {
        console.error('Failed to load data:', err);
        setError('Failed to load data from API');
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Toggle pipeline accordion
  const togglePipeline = (pipelineId) => {
    const newExpanded = new Set(expandedPipelines);
    if (newExpanded.has(pipelineId)) {
      newExpanded.delete(pipelineId);
    } else {
      newExpanded.add(pipelineId);
    }
    setExpandedPipelines(newExpanded);
  };

  // Scroll to specific pipeline based on URL query parameter and auto-expand
  useEffect(() => {
    if (!loading) {
      const params = new URLSearchParams(window.location.search);
      const pipelineId = params.get('id');

      if (pipelineId) {
        const fullPipelineId = `pipeline-${pipelineId}`;
        // Auto-expand the pipeline
        setExpandedPipelines(new Set([fullPipelineId]));

        setTimeout(() => {
          const element = document.getElementById(fullPipelineId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Add a brief highlight effect
            element.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.5)';
            setTimeout(() => {
              element.style.boxShadow = '';
            }, 2000);
          }
        }, 300);
      }
    }
  }, [loading]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ready': return faCheck;
      case 'processing': return faBolt;
      case 'validating': return faSearch;
      case 'failed': return faTimes;
      default: return faClock;
    }
  };

  const getFileTypeIcon = (type) => {
    if (type.includes('Sentinel')) return faSatellite;
    if (type.includes('Landsat')) return faGlobe;
    if (type.includes('NetCDF')) return faCloud;
    if (type.includes('DEM')) return faMountain;
    if (type.includes('Vector')) return faRuler;
    return faFile;
  };

  const toggleRow = (fileId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(fileId)) {
      newExpanded.delete(fileId);
    } else {
      newExpanded.add(fileId);
    }
    setExpandedRows(newExpanded);
  };

  const getQualityScoreClass = (score) => {
    if (score >= 90) return 'excellent';
    if (score >= 70) return 'good';
    return 'poor';
  };

  // Show loading state
  if (loading) {
    return (
      <div className="pipelines-container">
        <LoadingScreen message="Loading pipeline data..." />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="pipelines-container">
        <div className="pipelines-header">
          <h2>
            <FontAwesomeIcon icon={faProjectDiagram} style={{ marginRight: '0.5rem' }} />
            Data Processing Pipelines
          </h2>
        </div>
        <div className="error-container">
          <FontAwesomeIcon icon={faExclamationTriangle} className="error-icon" />
          <h3>Connection Error</h3>
          <p>{error}</p>
          <button className="retry-btn" onClick={() => window.location.reload()}>
            <FontAwesomeIcon icon={faRefresh} style={{ marginRight: '0.5rem' }} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pipelines-container">
      <div className="pipelines-header">
        <h2>
          <FontAwesomeIcon icon={faProjectDiagram} style={{ marginRight: '0.5rem' }} />
          My Data Pipelines
        </h2>
        <div className="pipeline-stats" style={{ display: 'flex', gap: '2rem', fontSize: '0.9rem' }}>
          <div>
            <span style={{ color: '#10b981', fontWeight: '600' }}>2</span>
            <span style={{ color: '#94a3b8', marginLeft: '0.5rem' }}>Running</span>
          </div>
          <div>
            <span style={{ color: '#3b82f6', fontWeight: '600' }}>4</span>
            <span style={{ color: '#94a3b8', marginLeft: '0.5rem' }}>Completed</span>
          </div>
        </div>
      </div>

      <div style={{ marginTop: '1.5rem' }}>
        <div className="data-table">
          <div className="table-header">
            <h3 className="table-title">My Custom Pipelines</h3>
            <span className="table-subtitle">Monitor and manage your custom EO processing workflows</span>
          </div>
          <div className="table-content">
            <div style={{display: 'flex', flexDirection: 'column', gap: '1.5rem'}}>

              {/* Pipeline 1: ECMWF → CDO */}
              <div id="pipeline-ecmwf-cdo" style={{
                background: 'rgba(15, 23, 42, 0.6)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '12px',
                transition: 'all 0.3s'
              }}>
                {/* Accordion Header - Always Visible */}
                <div
                  onClick={() => togglePipeline('pipeline-ecmwf-cdo')}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '1.5rem',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(16, 185, 129, 0.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                    <FontAwesomeIcon
                      icon={expandedPipelines.has('pipeline-ecmwf-cdo') ? faChevronDown : faChevronRight}
                      style={{ color: '#10b981', fontSize: '1rem' }}
                    />
                    <div>
                      <h3 style={{fontSize: '1.25rem', marginBottom: '0.25rem', color: '#e2e8f0'}}>
                        ECMWF Weather → CDO Processing
                      </h3>
                      <p style={{color: '#94a3b8', fontSize: '0.85rem'}}>ERA5 Reanalysis Data Processing Pipeline</p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                    <div style={{ fontSize: '0.85rem' }}>
                      <span style={{ color: '#64748b' }}>Progress: </span>
                      <span style={{ fontWeight: '600', color: '#10b981' }}>67%</span>
                    </div>
                    <div style={{padding: '0.5rem 1rem', background: 'rgba(16, 185, 129, 0.2)', border: '1px solid rgba(16, 185, 129, 0.4)', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '600', color: '#10b981'}}>
                      RUNNING
                    </div>
                  </div>
                </div>

                {/* Accordion Content - Expandable with Tabs */}
                {expandedPipelines.has('pipeline-ecmwf-cdo') && (<div style={{ borderTop: '1px solid rgba(51, 65, 85, 0.3)' }}>

                  {/* Pipeline Tabs */}
                  <div style={{ display: 'flex', gap: '0.5rem', padding: '1rem 1.5rem 0 1.5rem', borderBottom: '1px solid rgba(51, 65, 85, 0.3)' }}>
                    {['overview', 'stages', 'files', 'config'].map(tab => (
                      <button
                        key={tab}
                        onClick={() => setPipelineTab('pipeline-ecmwf-cdo', tab)}
                        style={{
                          padding: '0.5rem 1rem',
                          background: getPipelineTab('pipeline-ecmwf-cdo') === tab ? 'rgba(16, 185, 129, 0.2)' : 'transparent',
                          border: getPipelineTab('pipeline-ecmwf-cdo') === tab ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid transparent',
                          borderBottom: 'none',
                          borderRadius: '6px 6px 0 0',
                          color: getPipelineTab('pipeline-ecmwf-cdo') === tab ? '#10b981' : '#94a3b8',
                          fontSize: '0.85rem',
                          fontWeight: getPipelineTab('pipeline-ecmwf-cdo') === tab ? '600' : '400',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                      </button>
                    ))}
                  </div>

                  <div style={{ padding: '1.5rem' }}>
                    {getPipelineTab('pipeline-ecmwf-cdo') === 'overview' && (
                      <>
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem'}}>
                          <div>
                            <div style={{fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem'}}>Data Source</div>
                            <div style={{fontSize: '0.9rem', color: '#e2e8f0'}}>ECMWF ERA5 API</div>
                          </div>
                          <div>
                            <div style={{fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem'}}>Processing Tool</div>
                            <div style={{fontSize: '0.9rem', color: '#e2e8f0'}}>CDO (Climate Data Operators)</div>
                          </div>
                          <div>
                            <div style={{fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem'}}>Output Format</div>
                            <div style={{fontSize: '0.9rem', color: '#e2e8f0'}}>NetCDF Time Series</div>
                          </div>
                        </div>

                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '1rem', borderTop: '1px solid rgba(51, 65, 85, 0.5)'}}>
                          <div style={{display: 'flex', gap: '2rem', fontSize: '0.85rem'}}>
                            <div><span style={{color: '#64748b'}}>Progress:</span> <span style={{fontWeight: '600', color: '#10b981'}}>67%</span></div>
                            <div><span style={{color: '#64748b'}}>Started:</span> <span style={{color: '#e2e8f0'}}>12 min ago</span></div>
                            <div><span style={{color: '#64748b'}}>ETA:</span> <span style={{color: '#e2e8f0'}}>4 min</span></div>
                          </div>
                          <div style={{width: '150px', height: '8px', background: 'rgba(51, 65, 85, 0.5)', borderRadius: '4px', overflow: 'hidden'}}>
                            <div style={{width: '67%', height: '100%', background: 'linear-gradient(90deg, #10b981, #34d399)', borderRadius: '4px'}}></div>
                          </div>
                        </div>
                      </>
                    )}

                    {getPipelineTab('pipeline-ecmwf-cdo') === 'stages' && (
                      <div style={{background: 'rgba(15, 23, 42, 0.8)', padding: '1rem', borderRadius: '8px'}}>
                        <div style={{fontSize: '0.85rem', color: '#94a3b8', marginBottom: '0.75rem'}}>Processing Stages:</div>
                        <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.85rem'}}>
                          <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                            <span style={{color: '#10b981'}}>✓</span>
                            <span style={{color: '#cbd5e1'}}>Stage 1: Download ERA5 temperature data from ECMWF API</span>
                          </div>
                          <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                            <span style={{color: '#3b82f6'}}>→</span>
                            <span style={{color: '#cbd5e1'}}>Stage 2: Apply CDO spatial averaging over Europe region</span>
                          </div>
                          <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                            <span style={{color: '#64748b'}}>○</span>
                            <span style={{color: '#94a3b8'}}>Stage 3: Export to NetCDF timeseries format</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {getPipelineTab('pipeline-ecmwf-cdo') === 'files' && (
                      <div>
                        <div style={{fontSize: '0.9rem', color: '#e2e8f0', marginBottom: '1rem'}}>Incoming Files (3)</div>
                        <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
                          <div style={{padding: '0.75rem', background: 'rgba(15, 23, 42, 0.8)', borderRadius: '6px', display: 'flex', justifyContent: 'space-between'}}>
                            <span style={{color: '#cbd5e1'}}>ERA5_temp_2025-10-01.nc</span>
                            <span style={{color: '#10b981', fontSize: '0.85rem'}}>✓ Processed</span>
                          </div>
                          <div style={{padding: '0.75rem', background: 'rgba(15, 23, 42, 0.8)', borderRadius: '6px', display: 'flex', justifyContent: 'space-between'}}>
                            <span style={{color: '#cbd5e1'}}>ERA5_temp_2025-10-02.nc</span>
                            <span style={{color: '#3b82f6', fontSize: '0.85rem'}}>→ Processing</span>
                          </div>
                          <div style={{padding: '0.75rem', background: 'rgba(15, 23, 42, 0.8)', borderRadius: '6px', display: 'flex', justifyContent: 'space-between'}}>
                            <span style={{color: '#cbd5e1'}}>ERA5_temp_2025-10-03.nc</span>
                            <span style={{color: '#94a3b8', fontSize: '0.85rem'}}>○ Pending</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {getPipelineTab('pipeline-ecmwf-cdo') === 'config' && (
                      <div>
                        <div style={{fontSize: '0.9rem', color: '#e2e8f0', marginBottom: '1rem'}}>Pipeline Configuration</div>
                        <div style={{background: 'rgba(15, 23, 42, 0.8)', padding: '1rem', borderRadius: '8px', fontFamily: 'monospace', fontSize: '0.85rem', color: '#94a3b8'}}>
                          <div>Source: ECMWF ERA5 API</div>
                          <div>Region: Europe (35°N-70°N, 10°W-40°E)</div>
                          <div>Variables: temperature_2m</div>
                          <div>Temporal Resolution: hourly</div>
                          <div>Output: NetCDF-4 compressed</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>)}
              </div>

              {/* Pipeline 2: Sentinel-2 → NDVI */}
              <div id="pipeline-sentinel2-ndvi" style={{
                background: 'rgba(15, 23, 42, 0.6)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '12px',
                transition: 'all 0.3s'
              }}>
                {/* Accordion Header */}
                <div
                  onClick={() => togglePipeline('pipeline-sentinel2-ndvi')}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '1.5rem',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(16, 185, 129, 0.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                    <FontAwesomeIcon
                      icon={expandedPipelines.has('pipeline-sentinel2-ndvi') ? faChevronDown : faChevronRight}
                      style={{ color: '#10b981', fontSize: '1rem' }}
                    />
                    <div>
                      <h3 style={{fontSize: '1.25rem', marginBottom: '0.25rem', color: '#e2e8f0'}}>
                        Sentinel-2 → NDVI Analytics
                      </h3>
                      <p style={{color: '#94a3b8', fontSize: '0.85rem', margin: 0}}>Vegetation Index Monitoring for Agricultural Areas</p>
                    </div>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', gap: '1.5rem'}}>
                    <div style={{fontSize: '0.85rem', color: '#64748b'}}>
                      <span style={{color: '#10b981', fontWeight: '600'}}>Progress: </span>45%
                    </div>
                    <div style={{padding: '0.5rem 1rem', background: 'rgba(16, 185, 129, 0.2)', border: '1px solid rgba(16, 185, 129, 0.4)', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '600', color: '#10b981'}}>
                      RUNNING
                    </div>
                  </div>
                </div>

                {/* Accordion Content */}
                {expandedPipelines.has('pipeline-sentinel2-ndvi') && (<div style={{ borderTop: '1px solid rgba(51, 65, 85, 0.3)' }}>
                  {/* Pipeline Tabs */}
                  <div style={{ display: 'flex', gap: '0.5rem', padding: '1rem 1.5rem 0 1.5rem', borderBottom: '1px solid rgba(51, 65, 85, 0.3)' }}>
                    {['overview', 'stages', 'files', 'config'].map(tab => (
                      <button
                        key={tab}
                        onClick={() => setPipelineTab('pipeline-sentinel2-ndvi', tab)}
                        style={{
                          padding: '0.5rem 1rem',
                          background: getPipelineTab('pipeline-sentinel2-ndvi') === tab ? 'rgba(16, 185, 129, 0.2)' : 'transparent',
                          border: getPipelineTab('pipeline-sentinel2-ndvi') === tab ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid transparent',
                          borderBottom: 'none',
                          borderRadius: '6px 6px 0 0',
                          color: getPipelineTab('pipeline-sentinel2-ndvi') === tab ? '#10b981' : '#94a3b8',
                          fontSize: '0.85rem',
                          fontWeight: getPipelineTab('pipeline-sentinel2-ndvi') === tab ? '600' : '400',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                      </button>
                    ))}
                  </div>

                  <div style={{ padding: '1.5rem' }}>
                    {getPipelineTab('pipeline-sentinel2-ndvi') === 'overview' && (
                      <>
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem'}}>
                          <div>
                            <div style={{fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem'}}>Data Source</div>
                            <div style={{fontSize: '0.9rem', color: '#e2e8f0'}}>Sentinel-2 L2A (Copernicus)</div>
                          </div>
                          <div>
                            <div style={{fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem'}}>Processing Tool</div>
                            <div style={{fontSize: '0.9rem', color: '#e2e8f0'}}>Python (NumPy, Rasterio)</div>
                          </div>
                          <div>
                            <div style={{fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem'}}>Output Format</div>
                            <div style={{fontSize: '0.9rem', color: '#e2e8f0'}}>GeoTIFF + CSV Statistics</div>
                          </div>
                        </div>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '1rem', borderTop: '1px solid rgba(51, 65, 85, 0.5)'}}>
                          <div style={{display: 'flex', gap: '2rem', fontSize: '0.85rem'}}>
                            <div><span style={{color: '#64748b'}}>Started:</span> <span style={{color: '#e2e8f0'}}>8 min ago</span></div>
                            <div><span style={{color: '#64748b'}}>ETA:</span> <span style={{color: '#e2e8f0'}}>8 min</span></div>
                          </div>
                          <div style={{width: '200px', height: '8px', background: 'rgba(51, 65, 85, 0.5)', borderRadius: '4px', overflow: 'hidden'}}>
                            <div style={{width: '45%', height: '100%', background: 'linear-gradient(90deg, #10b981, #34d399)', borderRadius: '4px'}}></div>
                          </div>
                        </div>
                      </>
                    )}

                    {getPipelineTab('pipeline-sentinel2-ndvi') === 'stages' && (
                      <div style={{background: 'rgba(15, 23, 42, 0.8)', padding: '1rem', borderRadius: '8px'}}>
                        <div style={{fontSize: '0.85rem', color: '#94a3b8', marginBottom: '0.75rem'}}>Processing Stages:</div>
                        <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.85rem'}}>
                          <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                            <span style={{color: '#10b981'}}>✓</span>
                            <span style={{color: '#cbd5e1'}}>Step 1: Download Sentinel-2 L2A scenes from SciHub</span>
                          </div>
                          <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                            <span style={{color: '#3b82f6'}}>→</span>
                            <span style={{color: '#cbd5e1'}}>Step 2: Calculate NDVI using Python (NIR - Red) / (NIR + Red)</span>
                          </div>
                          <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                            <span style={{color: '#64748b'}}>○</span>
                            <span style={{color: '#94a3b8'}}>Step 3: Export GeoTIFF and compute zonal statistics</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {getPipelineTab('pipeline-sentinel2-ndvi') === 'files' && (
                      <div>
                        <div style={{fontSize: '0.9rem', color: '#e2e8f0', marginBottom: '1rem'}}>Incoming Files (2)</div>
                        <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
                          <div style={{padding: '0.75rem', background: 'rgba(15, 23, 42, 0.8)', borderRadius: '6px', display: 'flex', justifyContent: 'space-between'}}>
                            <span style={{color: '#cbd5e1'}}>S2A_MSIL2A_20251003_T35TKE.SAFE</span>
                            <span style={{color: '#10b981', fontSize: '0.85rem'}}>Processing</span>
                          </div>
                          <div style={{padding: '0.75rem', background: 'rgba(15, 23, 42, 0.8)', borderRadius: '6px', display: 'flex', justifyContent: 'space-between'}}>
                            <span style={{color: '#cbd5e1'}}>S2B_MSIL2A_20251001_T35TKE.SAFE</span>
                            <span style={{color: '#64748b', fontSize: '0.85rem'}}>Queued</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {getPipelineTab('pipeline-sentinel2-ndvi') === 'config' && (
                      <div>
                        <div style={{fontSize: '0.9rem', color: '#e2e8f0', marginBottom: '1rem'}}>Pipeline Configuration</div>
                        <div style={{background: 'rgba(15, 23, 42, 0.8)', padding: '1rem', borderRadius: '8px', fontFamily: 'monospace', fontSize: '0.85rem', color: '#94a3b8'}}>
                          <div>Source: Copernicus SciHub (Sentinel-2 L2A)</div>
                          <div>Region: Agricultural areas (Greece)</div>
                          <div>Cloud Cover: {'<'} 20%</div>
                          <div>Bands: B04 (Red), B08 (NIR)</div>
                          <div>Output: GeoTIFF + CSV statistics</div>
                          <div>Schedule: Daily at 06:00 UTC</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>)}
              </div>

              {/* Pipeline 3: MODIS → LST (Completed) */}
              <div id="pipeline-modis-lst" style={{
                background: 'rgba(15, 23, 42, 0.6)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '12px',
                transition: 'all 0.3s'
              }}>
                <div
                  onClick={() => togglePipeline('pipeline-modis-lst')}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '1.5rem',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(59, 130, 246, 0.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                    <FontAwesomeIcon
                      icon={expandedPipelines.has('pipeline-modis-lst') ? faChevronDown : faChevronRight}
                      style={{ color: '#3b82f6', fontSize: '1rem' }}
                    />
                    <div>
                      <h3 style={{fontSize: '1.25rem', marginBottom: '0.25rem', color: '#e2e8f0'}}>
                        MODIS → LST Time Series
                      </h3>
                      <p style={{color: '#94a3b8', fontSize: '0.85rem', margin: 0}}>Land Surface Temperature Analysis</p>
                    </div>
                  </div>
                  <div style={{padding: '0.5rem 1rem', background: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(59, 130, 246, 0.4)', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '600', color: '#3b82f6'}}>
                    COMPLETED
                  </div>
                </div>

                {expandedPipelines.has('pipeline-modis-lst') && (<div style={{ borderTop: '1px solid rgba(51, 65, 85, 0.3)' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', padding: '1rem 1.5rem 0 1.5rem', borderBottom: '1px solid rgba(51, 65, 85, 0.3)' }}>
                    {['overview', 'stages', 'files', 'config'].map(tab => (
                      <button
                        key={tab}
                        onClick={() => setPipelineTab('pipeline-modis-lst', tab)}
                        style={{
                          padding: '0.5rem 1rem',
                          background: getPipelineTab('pipeline-modis-lst') === tab ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
                          border: getPipelineTab('pipeline-modis-lst') === tab ? '1px solid rgba(59, 130, 246, 0.4)' : '1px solid transparent',
                          borderBottom: 'none',
                          borderRadius: '6px 6px 0 0',
                          color: getPipelineTab('pipeline-modis-lst') === tab ? '#3b82f6' : '#94a3b8',
                          fontSize: '0.85rem',
                          fontWeight: getPipelineTab('pipeline-modis-lst') === tab ? '600' : '400',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                      </button>
                    ))}
                  </div>

                  <div style={{ padding: '1.5rem' }}>
                    {getPipelineTab('pipeline-modis-lst') === 'overview' && (
                      <>
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem'}}>
                          <div>
                            <div style={{fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem'}}>Data Source</div>
                            <div style={{fontSize: '0.9rem', color: '#e2e8f0'}}>MODIS MOD11A1 (NASA)</div>
                          </div>
                          <div>
                            <div style={{fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem'}}>Files Processed</div>
                            <div style={{fontSize: '0.9rem', color: '#e2e8f0'}}>48 HDF5 files</div>
                          </div>
                          <div>
                            <div style={{fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem'}}>Output</div>
                            <div style={{fontSize: '0.9rem', color: '#e2e8f0'}}>Mosaic GeoTIFF</div>
                          </div>
                        </div>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '1rem', borderTop: '1px solid rgba(51, 65, 85, 0.5)'}}>
                          <div style={{display: 'flex', gap: '2rem', fontSize: '0.85rem'}}>
                            <div><span style={{color: '#64748b'}}>Runtime:</span> <span style={{color: '#e2e8f0'}}>12 min</span></div>
                            <div><span style={{color: '#64748b'}}>Completed:</span> <span style={{color: '#e2e8f0'}}>2 hours ago</span></div>
                            <div style={{color: '#10b981', fontWeight: '600'}}>✓ Success</div>
                          </div>
                        </div>
                      </>
                    )}

                    {getPipelineTab('pipeline-modis-lst') === 'stages' && (
                      <div style={{background: 'rgba(15, 23, 42, 0.8)', padding: '1rem', borderRadius: '8px'}}>
                        <div style={{fontSize: '0.85rem', color: '#94a3b8', marginBottom: '0.75rem'}}>Processing Stages:</div>
                        <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.85rem'}}>
                          <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                            <span style={{color: '#10b981'}}>✓</span>
                            <span style={{color: '#cbd5e1'}}>Step 1: Download MODIS MOD11A1 tiles from NASA LAADS DAAC</span>
                          </div>
                          <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                            <span style={{color: '#10b981'}}>✓</span>
                            <span style={{color: '#cbd5e1'}}>Step 2: Extract LST bands and apply scale factors</span>
                          </div>
                          <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                            <span style={{color: '#10b981'}}>✓</span>
                            <span style={{color: '#cbd5e1'}}>Step 3: Mosaic tiles and export as GeoTIFF</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {getPipelineTab('pipeline-modis-lst') === 'files' && (
                      <div>
                        <div style={{fontSize: '0.9rem', color: '#e2e8f0', marginBottom: '1rem'}}>Processed Files (48)</div>
                        <div style={{fontSize: '0.85rem', color: '#64748b'}}>
                          All 48 HDF5 files successfully processed and mosaicked into final GeoTIFF output.
                        </div>
                      </div>
                    )}

                    {getPipelineTab('pipeline-modis-lst') === 'config' && (
                      <div>
                        <div style={{fontSize: '0.9rem', color: '#e2e8f0', marginBottom: '1rem'}}>Pipeline Configuration</div>
                        <div style={{background: 'rgba(15, 23, 42, 0.8)', padding: '1rem', borderRadius: '8px', fontFamily: 'monospace', fontSize: '0.85rem', color: '#94a3b8'}}>
                          <div>Source: NASA LAADS DAAC (MODIS MOD11A1)</div>
                          <div>Region: Mediterranean (30°N-45°N, 0°E-30°E)</div>
                          <div>Product: Land Surface Temperature (LST)</div>
                          <div>Resolution: 1km</div>
                          <div>Output: Mosaicked GeoTIFF</div>
                          <div>Schedule: Weekly on Sundays</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>)}
              </div>

              {/* Pipeline 4: Landsat → Cloud Masking (Completed) */}
              <div id="pipeline-landsat-cloud" style={{
                background: 'rgba(15, 23, 42, 0.6)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '12px',
                transition: 'all 0.3s'
              }}>
                <div
                  onClick={() => togglePipeline('pipeline-landsat-cloud')}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '1.5rem',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(59, 130, 246, 0.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                    <FontAwesomeIcon
                      icon={expandedPipelines.has('pipeline-landsat-cloud') ? faChevronDown : faChevronRight}
                      style={{ color: '#3b82f6', fontSize: '1rem' }}
                    />
                    <div>
                      <h3 style={{fontSize: '1.25rem', marginBottom: '0.25rem', color: '#e2e8f0'}}>
                        Landsat-8 → Cloud Masking
                      </h3>
                      <p style={{color: '#94a3b8', fontSize: '0.85rem', margin: 0}}>QA Band Processing & Cloud-free Composite Generation</p>
                    </div>
                  </div>
                  <div style={{padding: '0.5rem 1rem', background: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(59, 130, 246, 0.4)', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '600', color: '#3b82f6'}}>
                    COMPLETED
                  </div>
                </div>

                {expandedPipelines.has('pipeline-landsat-cloud') && (<div style={{ borderTop: '1px solid rgba(51, 65, 85, 0.3)' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', padding: '1rem 1.5rem 0 1.5rem', borderBottom: '1px solid rgba(51, 65, 85, 0.3)' }}>
                    {['overview', 'config'].map(tab => (
                      <button
                        key={tab}
                        onClick={() => setPipelineTab('pipeline-landsat-cloud', tab)}
                        style={{
                          padding: '0.5rem 1rem',
                          background: getPipelineTab('pipeline-landsat-cloud') === tab ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
                          border: getPipelineTab('pipeline-landsat-cloud') === tab ? '1px solid rgba(59, 130, 246, 0.4)' : '1px solid transparent',
                          borderBottom: 'none',
                          borderRadius: '6px 6px 0 0',
                          color: getPipelineTab('pipeline-landsat-cloud') === tab ? '#3b82f6' : '#94a3b8',
                          fontSize: '0.85rem',
                          fontWeight: getPipelineTab('pipeline-landsat-cloud') === tab ? '600' : '400',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                      </button>
                    ))}
                  </div>

                  <div style={{ padding: '1.5rem' }}>
                    {getPipelineTab('pipeline-landsat-cloud') === 'overview' && (
                      <>
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem'}}>
                          <div>
                            <div style={{fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem'}}>Data Source</div>
                            <div style={{fontSize: '0.9rem', color: '#e2e8f0'}}>Landsat 8/9 Collection 2</div>
                          </div>
                          <div>
                            <div style={{fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem'}}>Scenes Processed</div>
                            <div style={{fontSize: '0.9rem', color: '#e2e8f0'}}>24 scenes</div>
                          </div>
                          <div>
                            <div style={{fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem'}}>Algorithm</div>
                            <div style={{fontSize: '0.9rem', color: '#e2e8f0'}}>Fmask 4.0</div>
                          </div>
                        </div>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '1rem', borderTop: '1px solid rgba(51, 65, 85, 0.5)'}}>
                          <div style={{display: 'flex', gap: '2rem', fontSize: '0.85rem'}}>
                            <div><span style={{color: '#64748b'}}>Runtime:</span> <span style={{color: '#e2e8f0'}}>18 min</span></div>
                            <div><span style={{color: '#64748b'}}>Completed:</span> <span style={{color: '#e2e8f0'}}>5 hours ago</span></div>
                            <div style={{color: '#10b981', fontWeight: '600'}}>✓ Success</div>
                          </div>
                        </div>
                      </>
                    )}

                    {getPipelineTab('pipeline-landsat-cloud') === 'config' && (
                      <div>
                        <div style={{fontSize: '0.9rem', color: '#e2e8f0', marginBottom: '1rem'}}>Pipeline Configuration</div>
                        <div style={{background: 'rgba(15, 23, 42, 0.8)', padding: '1rem', borderRadius: '8px', fontFamily: 'monospace', fontSize: '0.85rem', color: '#94a3b8'}}>
                          <div>Source: USGS Earth Explorer (Landsat 8/9 Collection 2)</div>
                          <div>Cloud Masking: Fmask 4.0 algorithm</div>
                          <div>Output: Cloud-free composite</div>
                          <div>Schedule: Weekly processing</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>)}
              </div>

              {/* Pipeline 5: DEM → Terrain (Completed) */}
              <div id="pipeline-dem-terrain" style={{
                background: 'rgba(15, 23, 42, 0.6)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '12px',
                transition: 'all 0.3s'
              }}>
                <div
                  onClick={() => togglePipeline('pipeline-dem-terrain')}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '1.5rem',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(59, 130, 246, 0.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                    <FontAwesomeIcon
                      icon={expandedPipelines.has('pipeline-dem-terrain') ? faChevronDown : faChevronRight}
                      style={{ color: '#3b82f6', fontSize: '1rem' }}
                    />
                    <div>
                      <h3 style={{fontSize: '1.25rem', marginBottom: '0.25rem', color: '#e2e8f0'}}>
                        Copernicus DEM → Terrain Analysis
                      </h3>
                      <p style={{color: '#94a3b8', fontSize: '0.85rem', margin: 0}}>Slope & Aspect Derivative Calculation</p>
                    </div>
                  </div>
                  <div style={{padding: '0.5rem 1rem', background: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(59, 130, 246, 0.4)', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '600', color: '#3b82f6'}}>
                    COMPLETED
                  </div>
                </div>

                {expandedPipelines.has('pipeline-dem-terrain') && (<div style={{ borderTop: '1px solid rgba(51, 65, 85, 0.3)' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', padding: '1rem 1.5rem 0 1.5rem', borderBottom: '1px solid rgba(51, 65, 85, 0.3)' }}>
                    {['overview', 'config'].map(tab => (
                      <button
                        key={tab}
                        onClick={() => setPipelineTab('pipeline-dem-terrain', tab)}
                        style={{
                          padding: '0.5rem 1rem',
                          background: getPipelineTab('pipeline-dem-terrain') === tab ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
                          border: getPipelineTab('pipeline-dem-terrain') === tab ? '1px solid rgba(59, 130, 246, 0.4)' : '1px solid transparent',
                          borderBottom: 'none',
                          borderRadius: '6px 6px 0 0',
                          color: getPipelineTab('pipeline-dem-terrain') === tab ? '#3b82f6' : '#94a3b8',
                          fontSize: '0.85rem',
                          fontWeight: getPipelineTab('pipeline-dem-terrain') === tab ? '600' : '400',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                      </button>
                    ))}
                  </div>

                  <div style={{ padding: '1.5rem' }}>
                    {getPipelineTab('pipeline-dem-terrain') === 'overview' && (
                      <>
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem'}}>
                          <div>
                            <div style={{fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem'}}>Data Source</div>
                            <div style={{fontSize: '0.9rem', color: '#e2e8f0'}}>Copernicus DEM 30m</div>
                          </div>
                          <div>
                            <div style={{fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem'}}>Tool</div>
                            <div style={{fontSize: '0.9rem', color: '#e2e8f0'}}>GDAL gdaldem</div>
                          </div>
                          <div>
                            <div style={{fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem'}}>Output Layers</div>
                            <div style={{fontSize: '0.9rem', color: '#e2e8f0'}}>3 (Slope, Aspect, Hillshade)</div>
                          </div>
                        </div>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '1rem', borderTop: '1px solid rgba(51, 65, 85, 0.5)'}}>
                          <div style={{display: 'flex', gap: '2rem', fontSize: '0.85rem'}}>
                            <div><span style={{color: '#64748b'}}>Runtime:</span> <span style={{color: '#e2e8f0'}}>5 min</span></div>
                            <div><span style={{color: '#64748b'}}>Completed:</span> <span style={{color: '#e2e8f0'}}>Yesterday</span></div>
                            <div style={{color: '#10b981', fontWeight: '600'}}>✓ Success</div>
                          </div>
                        </div>
                      </>
                    )}

                    {getPipelineTab('pipeline-dem-terrain') === 'config' && (
                      <div>
                        <div style={{fontSize: '0.9rem', color: '#e2e8f0', marginBottom: '1rem'}}>Pipeline Configuration</div>
                        <div style={{background: 'rgba(15, 23, 42, 0.8)', padding: '1rem', borderRadius: '8px', fontFamily: 'monospace', fontSize: '0.85rem', color: '#94a3b8'}}>
                          <div>Source: Copernicus DEM 30m (EU Digital Elevation Model)</div>
                          <div>Tool: GDAL gdaldem</div>
                          <div>Outputs: Slope, Aspect, Hillshade layers</div>
                          <div>Region: Greece</div>
                          <div>Schedule: On-demand</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>)}
              </div>

              {/* Pipeline 6: Multi-Source → ML (Failed) */}
              <div id="pipeline-crop-yield-ml" style={{
                background: 'rgba(15, 23, 42, 0.6)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '12px',
                transition: 'all 0.3s'
              }}>
                <div
                  onClick={() => togglePipeline('pipeline-crop-yield-ml')}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '1.5rem',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                    <FontAwesomeIcon
                      icon={expandedPipelines.has('pipeline-crop-yield-ml') ? faChevronDown : faChevronRight}
                      style={{ color: '#ef4444', fontSize: '1rem' }}
                    />
                    <div>
                      <h3 style={{fontSize: '1.25rem', marginBottom: '0.25rem', color: '#e2e8f0'}}>
                        Multi-Source → Crop Yield ML
                      </h3>
                      <p style={{color: '#94a3b8', fontSize: '0.85rem', margin: 0}}>Maize Yield Prediction using Random Forest</p>
                    </div>
                  </div>
                  <div style={{padding: '0.5rem 1rem', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.4)', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '600', color: '#ef4444'}}>
                    FAILED
                  </div>
                </div>

                {expandedPipelines.has('pipeline-crop-yield-ml') && (<div style={{ borderTop: '1px solid rgba(51, 65, 85, 0.3)' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', padding: '1rem 1.5rem 0 1.5rem', borderBottom: '1px solid rgba(51, 65, 85, 0.3)' }}>
                    {['overview', 'error', 'config'].map(tab => (
                      <button
                        key={tab}
                        onClick={() => setPipelineTab('pipeline-crop-yield-ml', tab)}
                        style={{
                          padding: '0.5rem 1rem',
                          background: getPipelineTab('pipeline-crop-yield-ml') === tab ? 'rgba(239, 68, 68, 0.2)' : 'transparent',
                          border: getPipelineTab('pipeline-crop-yield-ml') === tab ? '1px solid rgba(239, 68, 68, 0.4)' : '1px solid transparent',
                          borderBottom: 'none',
                          borderRadius: '6px 6px 0 0',
                          color: getPipelineTab('pipeline-crop-yield-ml') === tab ? '#ef4444' : '#94a3b8',
                          fontSize: '0.85rem',
                          fontWeight: getPipelineTab('pipeline-crop-yield-ml') === tab ? '600' : '400',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                      </button>
                    ))}
                  </div>

                  <div style={{ padding: '1.5rem' }}>
                    {getPipelineTab('pipeline-crop-yield-ml') === 'overview' && (
                      <>
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem'}}>
                          <div>
                            <div style={{fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem'}}>Input Data</div>
                            <div style={{fontSize: '0.9rem', color: '#e2e8f0'}}>S2 NDVI + Weather API</div>
                          </div>
                          <div>
                            <div style={{fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem'}}>ML Model</div>
                            <div style={{fontSize: '0.9rem', color: '#e2e8f0'}}>Random Forest (scikit-learn)</div>
                          </div>
                          <div>
                            <div style={{fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem'}}>Target Crop</div>
                            <div style={{fontSize: '0.9rem', color: '#e2e8f0'}}>Maize (Thessaly Plain)</div>
                          </div>
                        </div>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '1rem', borderTop: '1px solid rgba(51, 65, 85, 0.5)'}}>
                          <div style={{display: 'flex', gap: '2rem', fontSize: '0.85rem'}}>
                            <div><span style={{color: '#64748b'}}>Runtime:</span> <span style={{color: '#e2e8f0'}}>22 min</span></div>
                            <div><span style={{color: '#64748b'}}>Failed:</span> <span style={{color: '#e2e8f0'}}>30 min ago</span></div>
                            <div style={{color: '#ef4444', fontWeight: '600'}}>Manual intervention required</div>
                          </div>
                        </div>
                      </>
                    )}

                    {getPipelineTab('pipeline-crop-yield-ml') === 'error' && (
                      <div>
                        <div style={{background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem'}}>
                          <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem'}}>
                            <FontAwesomeIcon icon={faExclamationTriangle} style={{color: '#ef4444', fontSize: '1.5rem'}} />
                            <div style={{fontSize: '1rem', color: '#f87171', fontWeight: '600'}}>Pipeline Failure: Weather API Connection Error</div>
                          </div>
                          <div style={{fontSize: '0.9rem', color: '#fca5a5', marginBottom: '1.5rem', lineHeight: '1.6'}}>
                            <div style={{marginBottom: '0.75rem'}}><strong>Primary Error:</strong> Weather API endpoint returned HTTP 404 (Not Found)</div>
                            <div style={{marginBottom: '0.75rem'}}><strong>Affected Component:</strong> Feature matrix builder for precipitation data</div>
                            <div style={{marginBottom: '0.75rem'}}><strong>Impact:</strong> Unable to complete ML model training - missing 847 precipitation data points for Thessaly region (Oct 2024 - Mar 2025)</div>
                            <div><strong>Timestamp:</strong> 2025-10-03 11:22:47 UTC</div>
                          </div>

                          <div style={{borderTop: '1px solid rgba(239, 68, 68, 0.2)', paddingTop: '1rem', marginTop: '1rem'}}>
                            <div style={{fontSize: '0.85rem', color: '#cbd5e1', fontWeight: '600', marginBottom: '0.75rem'}}>Stack Trace (last 3 calls):</div>
                            <div style={{background: 'rgba(0, 0, 0, 0.3)', padding: '0.75rem', borderRadius: '6px', fontFamily: 'monospace', fontSize: '0.75rem', color: '#94a3b8'}}>
                              <div>File: /opt/pipelines/ml/feature_builder.py:243</div>
                              <div>Function: fetch_weather_data()</div>
                              <div style={{color: '#ef4444'}}>HTTPError: 404 Client Error: Not Found for url: https://api.weather.eu/v2/precip...</div>
                            </div>
                          </div>

                          <div style={{borderTop: '1px solid rgba(239, 68, 68, 0.2)', paddingTop: '1rem', marginTop: '1rem'}}>
                            <div style={{fontSize: '0.85rem', color: '#cbd5e1', fontWeight: '600', marginBottom: '0.75rem'}}>Recommended Actions:</div>
                            <div style={{fontSize: '0.85rem', color: '#94a3b8', lineHeight: '1.8'}}>
                              <div>1. Verify Weather API endpoint URL and API key validity</div>
                              <div>2. Check API service status at https://status.weather.eu</div>
                              <div>3. Consider fallback to alternative weather data source (ECMWF ERA5)</div>
                              <div>4. Review related logs in <Link to="/logs" style={{color: '#3b82f6', textDecoration: 'underline'}}>Logs → ML Pipeline Errors</Link></div>
                            </div>
                          </div>
                        </div>

                        <Link to="/debugging" style={{textDecoration: 'none'}}>
                          <button style={{
                            width: '100%',
                            padding: '0.75rem',
                            background: 'rgba(59, 130, 246, 0.2)',
                            border: '1px solid rgba(59, 130, 246, 0.4)',
                            borderRadius: '6px',
                            color: '#3b82f6',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(59, 130, 246, 0.3)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)'}
                          >
                            <FontAwesomeIcon icon={faRobot} style={{marginRight: '0.5rem'}} />
                            Open in Debugging Assistant for AI-Powered Analysis
                          </button>
                        </Link>
                      </div>
                    )}

                    {getPipelineTab('pipeline-crop-yield-ml') === 'config' && (
                      <div>
                        <div style={{fontSize: '0.9rem', color: '#e2e8f0', marginBottom: '1rem'}}>Pipeline Configuration</div>
                        <div style={{background: 'rgba(15, 23, 42, 0.8)', padding: '1rem', borderRadius: '8px', fontFamily: 'monospace', fontSize: '0.85rem', color: '#94a3b8'}}>
                          <div>Source 1: Sentinel-2 NDVI (Copernicus)</div>
                          <div>Source 2: Weather API (precipitation, temperature)</div>
                          <div>ML Model: Random Forest Regressor (sklearn)</div>
                          <div>Target: Maize yield prediction (tons/hectare)</div>
                          <div>Region: Thessaly Plain, Greece</div>
                          <div>Training Period: 2020-2024 historical data</div>
                          <div>Schedule: Monthly retrain</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>)}
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Removed old tabs - keeping only My Pipelines view */}
      {false && (
        <div className="data-table">
          <div className="table-header">
            <h3 className="table-title">Pipeline Processing Stages</h3>
            <span className="table-subtitle">Real-time view of data processing pipeline stages and throughput</span>
            <div className="pipeline-controls">
              <button className="control-btn active">
                <FontAwesomeIcon icon={faPlay} style={{ marginRight: '0.25rem' }} />
                Running
              </button>
              <button className="control-btn">
                <FontAwesomeIcon icon={faFilter} style={{ marginRight: '0.25rem' }} />
                Filter
              </button>
            </div>
          </div>
          <div className="table-content">
            <div className="pipeline-flow">
              {/* Stage 1: Ingestion */}
              <div className="pipeline-stage active">
                <div className="stage-header">
                  <FontAwesomeIcon icon={faFolder} className="stage-icon" />
                  <h4>1. Ingestion</h4>
                  <span className="stage-status healthy">Active</span>
                </div>
                <div className="stage-metrics">
                  <div className="metric">
                    <span className="metric-value">24</span>
                    <span className="metric-label">Scenes/Hour</span>
                  </div>
                  <div className="metric">
                    <span className="metric-value">2.3 GB</span>
                    <span className="metric-label">Throughput</span>
                  </div>
                  <div className="metric">
                    <span className="metric-value">S2/L8/MODIS</span>
                    <span className="metric-label">Missions</span>
                  </div>
                </div>
                <div className="stage-progress">
                  <svg width="200" height="40" viewBox="0 0 200 40" style={{display: 'block', marginTop: '1rem'}}>
                    <polyline
                      points="0,20 30,20 38,10 48,30 56,20 190,20"
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="heartbeat-line"
                    />
                    <circle cx="195" cy="20" r="4" fill="#10b981" className="heartbeat-dot"/>
                  </svg>
                </div>
              </div>

              {/* Stage 2: Validation */}
              <div className="pipeline-stage processing">
                <div className="stage-header">
                  <FontAwesomeIcon icon={faSearch} className="stage-icon" />
                  <h4>2. Validation</h4>
                  <span className="stage-status processing">Processing</span>
                </div>
                <div className="stage-metrics">
                  <div className="metric">
                    <span className="metric-value">94%</span>
                    <span className="metric-label">Pass Rate</span>
                  </div>
                  <div className="metric">
                    <span className="metric-value">CRS/Cloud/NoData</span>
                    <span className="metric-label">Checks</span>
                  </div>
                  <div
                    className="metric metric-clickable"
                    onClick={() => setShowFailedModal(true)}
                    style={{ cursor: 'pointer' }}
                    title="Click to view failed items"
                  >
                    <span className="metric-value" style={{ color: '#ef4444' }}>3</span>
                    <span className="metric-label">Failed ▼</span>
                  </div>
                </div>
                <div className="stage-progress">
                  <svg width="200" height="40" viewBox="0 0 200 40" style={{display: 'block', marginTop: '1rem'}}>
                    <polyline
                      points="0,20 30,20 38,10 48,30 56,20 190,20"
                      fill="none"
                      stroke="#f59e0b"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="heartbeat-line"
                    />
                    <circle cx="195" cy="20" r="4" fill="#f59e0b" className="heartbeat-dot"/>
                  </svg>
                </div>
              </div>

              {/* Stage 3: L2A Processing */}
              <div className="pipeline-stage active">
                <div className="stage-header">
                  <FontAwesomeIcon icon={faCogs} className="stage-icon" />
                  <h4>3. L2A (AC/Cloud Mask)</h4>
                  <span className="stage-status healthy">Active</span>
                </div>
                <div className="stage-metrics">
                  <div className="metric">
                    <span className="metric-value">Sen2Cor</span>
                    <span className="metric-label">Processor</span>
                  </div>
                  <div className="metric">
                    <span className="metric-value">98.2%</span>
                    <span className="metric-label">Success</span>
                  </div>
                  <div className="metric">
                    <span className="metric-value">2</span>
                    <span className="metric-label">Queue</span>
                  </div>
                </div>
                <div className="stage-progress">
                  <svg width="200" height="40" viewBox="0 0 200 40" style={{display: 'block', marginTop: '1rem'}}>
                    <polyline
                      points="0,20 30,20 38,10 48,30 56,20 190,20"
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="heartbeat-line"
                    />
                    <circle cx="195" cy="20" r="4" fill="#10b981" className="heartbeat-dot"/>
                  </svg>
                </div>
              </div>

              {/* Stage 4: Reprojection */}
              <div className="pipeline-stage active">
                <div className="stage-header">
                  <FontAwesomeIcon icon={faCogs} className="stage-icon" />
                  <h4>4. Reprojection</h4>
                  <span className="stage-status healthy">Active</span>
                </div>
                <div className="stage-metrics">
                  <div className="metric">
                    <span className="metric-value">gdalwarp</span>
                    <span className="metric-label">Tool</span>
                  </div>
                  <div className="metric">
                    <span className="metric-value">bilinear</span>
                    <span className="metric-label">Method</span>
                  </div>
                  <div className="metric">
                    <span className="metric-value">99.5%</span>
                    <span className="metric-label">Success</span>
                  </div>
                </div>
                <div className="stage-progress">
                  <svg width="200" height="40" viewBox="0 0 200 40" style={{display: 'block', marginTop: '1rem'}}>
                    <polyline
                      points="0,20 30,20 38,10 48,30 56,20 190,20"
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="heartbeat-line"
                    />
                    <circle cx="195" cy="20" r="4" fill="#10b981" className="heartbeat-dot"/>
                  </svg>
                </div>
              </div>

              {/* Stage 5: Mosaicking */}
              <div className="pipeline-stage active">
                <div className="stage-header">
                  <FontAwesomeIcon icon={faCogs} className="stage-icon" />
                  <h4>5. Mosaicking</h4>
                  <span className="stage-status healthy">Active</span>
                </div>
                <div className="stage-metrics">
                  <div className="metric">
                    <span className="metric-value">12</span>
                    <span className="metric-label">Mosaics/Day</span>
                  </div>
                  <div className="metric">
                    <span className="metric-value">gdalbuildvrt</span>
                    <span className="metric-label">Tool</span>
                  </div>
                  <div className="metric">
                    <span className="metric-value">99.8%</span>
                    <span className="metric-label">Success</span>
                  </div>
                </div>
                <div className="stage-progress">
                  <svg width="200" height="40" viewBox="0 0 200 40" style={{display: 'block', marginTop: '1rem'}}>
                    <polyline
                      points="0,20 30,20 38,10 48,30 56,20 190,20"
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="heartbeat-line"
                    />
                    <circle cx="195" cy="20" r="4" fill="#10b981" className="heartbeat-dot"/>
                  </svg>
                </div>
              </div>

              {/* Stage 6: COG/Pyramids */}
              <div className="pipeline-stage active">
                <div className="stage-header">
                  <FontAwesomeIcon icon={faCogs} className="stage-icon" />
                  <h4>6. COG/Pyramids</h4>
                  <span className="stage-status healthy">Active</span>
                </div>
                <div className="stage-metrics">
                  <div className="metric">
                    <span className="metric-value">DEFLATE</span>
                    <span className="metric-label">Compress</span>
                  </div>
                  <div className="metric">
                    <span className="metric-value">512x512</span>
                    <span className="metric-label">Tile Size</span>
                  </div>
                  <div className="metric">
                    <span className="metric-value">100%</span>
                    <span className="metric-label">Built</span>
                  </div>
                </div>
                <div className="stage-progress">
                  <svg width="200" height="40" viewBox="0 0 200 40" style={{display: 'block', marginTop: '1rem'}}>
                    <polyline
                      points="0,20 30,20 38,10 48,30 56,20 190,20"
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="heartbeat-line"
                    />
                    <circle cx="195" cy="20" r="4" fill="#10b981" className="heartbeat-dot"/>
                  </svg>
                </div>
              </div>

              {/* Stage 7: STAC Publish */}
              <div className="pipeline-stage active">
                <div className="stage-header">
                  <FontAwesomeIcon icon={faGlobe} className="stage-icon" />
                  <h4>7. STAC Publish</h4>
                  <span className="stage-status healthy">Active</span>
                </div>
                <div className="stage-metrics">
                  <div className="metric">
                    <span className="metric-value">18</span>
                    <span className="metric-label">Items/Hour</span>
                  </div>
                  <div className="metric">
                    <span className="metric-value">99.8%</span>
                    <span className="metric-label">Valid</span>
                  </div>
                  <div className="metric">
                    <span className="metric-value">0</span>
                    <span className="metric-label">Invalid</span>
                  </div>
                </div>
                <div className="stage-progress">
                  <svg width="200" height="40" viewBox="0 0 200 40" style={{display: 'block', marginTop: '1rem'}}>
                    <polyline
                      points="0,20 30,20 38,10 48,30 56,20 190,20"
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="heartbeat-line"
                    />
                    <circle cx="195" cy="20" r="4" fill="#10b981" className="heartbeat-dot"/>
                  </svg>
                </div>
              </div>
            </div>

            {/* Stage Details Section */}
            <div className="stage-details-section">
              <h4>
                <FontAwesomeIcon icon={faBolt} style={{ marginRight: '0.5rem' }} />
                Current Processing Activity
              </h4>
              <div className="processing-queue">
                <div className="queue-item">
                  <FontAwesomeIcon icon={faSatellite} className="file-icon" />
                  <div className="file-info">
                    <span className="file-name">sentinel2_L2A_20241201_T35ULA.tif</span>
                    <span className="file-stage">Stage 2: Validation</span>
                  </div>
                  <div className="processing-time">2m 34s</div>
                  <div className="progress-mini">
                    <div className="progress-bar-mini" style={{width: '67%'}}></div>
                  </div>
                </div>
                <div className="queue-item">
                  <FontAwesomeIcon icon={faCloud} className="file-icon" />
                  <div className="file-info">
                    <span className="file-name">precipitation_forecast_2024.nc</span>
                    <span className="file-stage">Stage 3: Transformation</span>
                  </div>
                  <div className="processing-time">5m 12s</div>
                  <div className="progress-mini">
                    <div className="progress-bar-mini" style={{width: '23%'}}></div>
                  </div>
                </div>
                <div className="queue-item">
                  <FontAwesomeIcon icon={faMountain} className="file-icon" />
                  <div className="file-info">
                    <span className="file-name">elevation_model_europe.tif</span>
                    <span className="file-stage">Stage 4: Publishing</span>
                  </div>
                  <div className="processing-time">1m 45s</div>
                  <div className="progress-mini">
                    <div className="progress-bar-mini" style={{width: '89%'}}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {false && (
        <div className="data-table">
          <div className="table-header">
            <h3 className="table-title">Pipeline Analytics & Performance</h3>
            <span className="table-subtitle">Historical performance metrics and trend analysis</span>
          </div>
          <div className="table-content">
            <div className="analytics-overview">
              <div className="analytics-card">
                <h4>
                  <FontAwesomeIcon icon={faChartLine} style={{ marginRight: '0.5rem' }} />
                  Processing Statistics (Last 30 Days)
                </h4>
                <div className="analytics-metrics">
                  <div className="analytics-metric">
                    <span className="metric-value">12,847</span>
                    <span className="metric-label">Files Processed</span>
                    <span className="metric-trend positive">+12% from last month</span>
                  </div>
                  <div className="analytics-metric">
                    <span className="metric-value">96.8%</span>
                    <span className="metric-label">Success Rate</span>
                    <span className="metric-trend positive">+2.1% improvement</span>
                  </div>
                  <div className="analytics-metric">
                    <span className="metric-value">4m 23s</span>
                    <span className="metric-label">Avg Processing Time</span>
                    <span className="metric-trend negative">+23s slower</span>
                  </div>
                  <div className="analytics-metric">
                    <span className="metric-value">847 GB</span>
                    <span className="metric-label">Data Volume</span>
                    <span className="metric-trend positive">+18% increase</span>
                  </div>
                </div>
              </div>

              <div className="analytics-card">
                <h4>
                  <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '0.5rem' }} />
                  Error Analysis
                </h4>
                <div className="error-breakdown">
                  <div className="error-category">
                    <span className="error-type">Format Validation Errors</span>
                    <span className="error-count">127 (2.1%)</span>
                    <span className="error-trend">-15% from last month</span>
                  </div>
                  <div className="error-category">
                    <span className="error-type">Coordinate System Issues</span>
                    <span className="error-count">84 (1.4%)</span>
                    <span className="error-trend">-8% from last month</span>
                  </div>
                  <div className="error-category">
                    <span className="error-type">File Corruption</span>
                    <span className="error-count">23 (0.4%)</span>
                    <span className="error-trend">+12% from last month</span>
                  </div>
                </div>
              </div>

              <div className="analytics-card">
                <h4>
                  <FontAwesomeIcon icon={faSatellite} style={{ marginRight: '0.5rem' }} />
                  Data Source Performance
                </h4>
                <div className="source-performance">
                  <div className="source-item">
                    <span className="source-name">Sentinel-2 L2A</span>
                    <span className="source-volume">4,234 files</span>
                    <span className="source-success">98.2% success</span>
                  </div>
                  <div className="source-item">
                    <span className="source-name">Landsat 8/9</span>
                    <span className="source-volume">3,187 files</span>
                    <span className="source-success">97.8% success</span>
                  </div>
                  <div className="source-item">
                    <span className="source-name">Weather Data</span>
                    <span className="source-volume">2,945 files</span>
                    <span className="source-success">99.1% success</span>
                  </div>
                  <div className="source-item">
                    <span className="source-name">DEM/Elevation</span>
                    <span className="source-volume">1,823 files</span>
                    <span className="source-success">94.7% success</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {false && (
        <div className="data-table">
          <div className="table-header">
            <h3 className="table-title">Incoming Geospatial Files ({pipelineData.length} files)</h3>
            <span className="table-subtitle">Click rows to view detailed validation results and metadata</span>
          </div>
          <div className="table-content">
            <table className="log-table">
              <thead>
                <tr>
                  <th style={{ width: '300px' }}>Filename</th>
                  <th style={{ width: '120px' }}>Type</th>
                  <th style={{ width: '100px' }}>Status</th>
                  <th style={{ width: '100px' }}>Quality Score</th>
                  <th style={{ width: '100px' }}>Size</th>
                </tr>
              </thead>
              <tbody>
                {pipelineData.map(file => (
                  <React.Fragment key={file.id}>
                    <tr
                      className={`expandable ${expandedRows.has(file.id) ? 'expanded' : ''}`}
                      onClick={() => toggleRow(file.id)}
                    >
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <FontAwesomeIcon icon={getFileTypeIcon(file.type)} />
                          <span style={{ fontWeight: 500 }}>{file.filename}</span>
                        </div>
                      </td>
                      <td style={{ color: '#94a3b8' }}>{file.type}</td>
                      <td>
                        <span className={`severity-badge ${file.status} ${file.status === 'processing' || file.status === 'validating' ? 'processing-badge' : ''}`}>
                          <FontAwesomeIcon icon={getStatusIcon(file.status)} style={{ marginRight: '0.25rem' }} />
                          {file.status.toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <span className={`quality-score ${getQualityScoreClass(file.validation.qualityScore)}`}>
                          {file.validation.qualityScore}%
                        </span>
                      </td>
                      <td style={{ fontFamily: 'monospace', color: '#94a3b8' }}>{file.size}</td>
                    </tr>
                    {expandedRows.has(file.id) && (
                      <tr className="expandable-row">
                        <td colSpan="5">
                          <div className="expandable-content">
                            <div className="file-details-grid">
                              <div className="properties-section">
                                <h4>
                                  <FontAwesomeIcon icon={faRuler} style={{ marginRight: '0.5rem' }} />
                                  Spatial Properties
                                </h4>
                                <div className="properties-grid">
                                  <div className="property-item">
                                    <span className="prop-label">CRS:</span>
                                    <span className="prop-value">{file.crs}</span>
                                  </div>
                                  {file.bands && (
                                    <div className="property-item">
                                      <span className="prop-label">Bands:</span>
                                      <span className="prop-value">{file.bands}</span>
                                    </div>
                                  )}
                                  {file.resolution && (
                                    <div className="property-item">
                                      <span className="prop-label">Resolution:</span>
                                      <span className="prop-value">{file.resolution}</span>
                                    </div>
                                  )}
                                  {file.features && (
                                    <div className="property-item">
                                      <span className="prop-label">Features:</span>
                                      <span className="prop-value">{file.features.toLocaleString()}</span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="validation-section">
                                <h4>
                                  <FontAwesomeIcon icon={faSearch} style={{ marginRight: '0.5rem' }} />
                                  Validation Results
                                </h4>
                                <div className="validation-rules">
                                  {file.rules.map((rule, index) => (
                                    <div key={index} className="rule-result">
                                      <div className="rule-header">
                                        <span className="rule-name">{rule.name}</span>
                                        <span className={`rule-status ${rule.status}`}>
                                          <FontAwesomeIcon
                                            icon={rule.status === 'passed' ? faCheck : rule.status === 'warning' ? faExclamationTriangle : faTimes}
                                            style={{ marginRight: '0.25rem' }}
                                          />
                                          {rule.status.toUpperCase()}
                                        </span>
                                      </div>
                                      <div className="rule-details">
                                        <span>Expected: {rule.expected}</span>
                                        <span>Actual: {rule.actual}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>

                            {file.error && (
                              <div className="ai-suggestion">
                                <div className="ai-suggestion-header">
                                  <FontAwesomeIcon icon={faRobot} />
                                  <strong>File Processing Error</strong>
                                  <span className="confidence-badge">Action Required</span>
                                </div>
                                <div className="ai-suggestion-content">
                                  <p><strong>Error:</strong> {file.error}</p>
                                  <p><strong>Recommendation:</strong> Check file format and projection settings. Ensure the file is not corrupted.</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {false && (
        <div className="data-table">
          <div className="table-header">
            <h3 className="table-title">Validation Rule Sets ({validationRules.length} rule sets)</h3>
            <button className="refresh-btn">+ Add Rule Set</button>
          </div>
          <div className="table-content">
            <div className="rules-grid">
              {validationRules.map(ruleSet => (
                <div key={ruleSet.id} className="rule-set-card">
                  <div className="rule-set-header">
                    <h4>{ruleSet.name}</h4>
                    <span className="file-pattern">{ruleSet.filePattern}</span>
                  </div>
                  <div className="rule-set-rules">
                    {ruleSet.rules.map((rule, index) => (
                      <div key={index} className="rule-item">
                        <span className="rule-field">{rule.field}</span>
                        <span className="rule-operator">{rule.operator}</span>
                        <span className="rule-value">{rule.value}</span>
                        <span className={`rule-severity ${rule.severity}`}>
                          {rule.severity}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="rule-set-actions">
                    <button className="edit-btn">Edit</button>
                    <button className="test-btn">Test</button>
                    <button className="delete-btn">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Failed Items Modal */}
      {showFailedModal && (
        <div className="alert-modal-overlay" onClick={() => setShowFailedModal(false)}>
          <div className="alert-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
            <div className="alert-modal-header">
              <div className="modal-title">
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#ef4444' }} />
                <h3>Failed Validation Items</h3>
              </div>
              <button className="modal-close" onClick={() => setShowFailedModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="alert-modal-content">
              <div className="alert-modal-section">
                <p style={{ color: '#94a3b8', marginBottom: '1.5rem' }}>
                  {failedItems.length} items failed validation checks. Review the details below to understand the issues and take corrective action.
                </p>

                {failedItems.map((item, idx) => (
                  <div key={item.id} className="failed-item-card" style={{
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '8px',
                    padding: '1.5rem',
                    marginBottom: idx < failedItems.length - 1 ? '1rem' : 0
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                          <FontAwesomeIcon icon={faSatellite} style={{ color: '#ef4444' }} />
                          <h4 style={{ margin: 0, color: '#f1f5f9', fontSize: '1rem' }}>{item.filename}</h4>
                        </div>
                        <div style={{ display: 'flex', gap: '1rem', fontSize: '0.85rem', color: '#94a3b8' }}>
                          <span><strong>Mission:</strong> {item.mission}</span>
                          <span><strong>Size:</strong> {item.fileSize}</span>
                          <span><strong>Time:</strong> {item.timestamp}</span>
                        </div>
                      </div>
                      <span style={{
                        background: 'rgba(239, 68, 68, 0.2)',
                        color: '#ef4444',
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        whiteSpace: 'nowrap'
                      }}>
                        FAILED
                      </span>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{
                        background: 'rgba(15, 23, 42, 0.8)',
                        border: '1px solid rgba(51, 65, 85, 0.3)',
                        borderRadius: '6px',
                        padding: '0.75rem 1rem',
                        marginBottom: '0.5rem'
                      }}>
                        <div style={{ fontWeight: 600, color: '#f87171', marginBottom: '0.25rem' }}>Error:</div>
                        <div style={{ color: '#e2e8f0' }}>{item.error}</div>
                      </div>
                      <div style={{
                        background: 'rgba(15, 23, 42, 0.8)',
                        border: '1px solid rgba(51, 65, 85, 0.3)',
                        borderRadius: '6px',
                        padding: '0.75rem 1rem',
                        fontSize: '0.85rem',
                        lineHeight: 1.6
                      }}>
                        <div style={{ fontWeight: 600, color: '#94a3b8', marginBottom: '0.25rem' }}>Details:</div>
                        <div style={{ color: '#cbd5e1' }}>{item.details}</div>
                      </div>
                    </div>

                    {(item.affectedBands || item.cloudCover || item.noDataPercentage) && (
                      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                        {item.affectedBands && (
                          <span style={{
                            background: 'rgba(239, 68, 68, 0.15)',
                            padding: '0.35rem 0.75rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            color: '#fca5a5'
                          }}>
                            Affected Bands: {item.affectedBands.join(', ')}
                          </span>
                        )}
                        {item.cloudCover && (
                          <span style={{
                            background: 'rgba(239, 68, 68, 0.15)',
                            padding: '0.35rem 0.75rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            color: '#fca5a5'
                          }}>
                            Cloud Coverage: {item.cloudCover}
                          </span>
                        )}
                        {item.noDataPercentage && (
                          <span style={{
                            background: 'rgba(239, 68, 68, 0.15)',
                            padding: '0.35rem 0.75rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            color: '#fca5a5'
                          }}>
                            NoData: {item.noDataPercentage}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="alert-modal-footer">
              <button className="modal-btn primary" onClick={() => setShowFailedModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pipelines;